import { getAuth } from "firebase/auth";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";

interface WebSocketContextType {
  ws: WebSocket | null;
  sendMessage: (message: string) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

export const WebSocketProvider: React.FC<{
  url: string;
  children: ReactNode;
}> = ({ url, children }) => {
  const [ws, setWs] = useState<WebSocket | null>(null);

  const getToken = useCallback(async () => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    return token || "";
  }, []);

  useEffect(() => {
    let wsInstance: WebSocket | null = null;
    let heartbeat: NodeJS.Timeout;
    let isMounted = true;

    const connect = async () => {
      try {
        const token = await getToken();
        const newWs = new WebSocket(`${url}?token=${token}`);

        newWs.onopen = () => {
          if (!isMounted) return;
          heartbeat = setInterval(() => {
            if (newWs.readyState === WebSocket.OPEN) {
              newWs.send(JSON.stringify({ type: "ping" }));
            }
          }, 5000);
        };
        newWs.onclose = () => {
          if (!isMounted) return;
          clearInterval(heartbeat);
          setTimeout(connect, 5000);
        };

        newWs.onerror = (error: Event) => {
          console.error("WebSocket error:", error);
        };

        if (isMounted) {
          setWs(newWs);
          wsInstance = newWs;
        }
      } catch (error) {
        console.error("Failed to connect:", error);
        setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      isMounted = false;
      clearInterval(heartbeat);
      if (wsInstance) {
        wsInstance.close();
      }
    };
  }, [url, getToken]);
  const sendMessage = useCallback(
    (message: string) => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(message);
      }
    },
    [ws]
  );

  const value = useMemo(() => ({ ws, sendMessage }), [ws, sendMessage]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("");
  }
  return context;
};
